import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { LocalWorkOrderItem, createLocalItem, calculateItemTotal } from "@/hooks/use-work-order-items";
import { formatBrazilianNumber, parseBrazilianCurrency } from "@/lib/utils";

interface WorkOrderItemsEditorProps {
  items: LocalWorkOrderItem[];
  onChange: (items: LocalWorkOrderItem[]) => void;
  disabled?: boolean;
}

export function WorkOrderItemsEditor({ items, onChange, disabled = false }: WorkOrderItemsEditorProps) {
  const handleAddItem = () => {
    onChange([...items, createLocalItem()]);
  };

  const handleRemoveItem = (tempId: string) => {
    onChange(items.filter(item => item.tempId !== tempId));
  };

  const handleUpdateItem = (tempId: string, field: keyof LocalWorkOrderItem, value: string | number) => {
    onChange(items.map(item => {
      if (item.tempId !== tempId) return item;
      
      const updated = { ...item, [field]: value };
      
      // Recalcular total quando quantidade ou valor unitário mudar
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? Number(value) : item.quantity;
        const price = field === 'unit_price' ? Number(value) : item.unit_price;
        updated.total_price = calculateItemTotal(qty, price);
      }
      
      return updated;
    }));
  };

  const handleCurrencyChange = (tempId: string, field: 'unit_price', rawValue: string) => {
    // Permite apenas números, vírgula e ponto
    if (!/^[\d.,]*$/.test(rawValue)) return;
    
    const numericValue = parseBrazilianCurrency(rawValue);
    handleUpdateItem(tempId, field, numericValue);
  };

  const totalGeral = items.reduce((sum, item) => sum + item.total_price, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Itens do Serviço</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddItem}
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-1" />
          Adicionar
        </Button>
      </div>

      {items.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[80px]">Qtd</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="w-[120px] text-right">Valor Unit.</TableHead>
                <TableHead className="w-[120px] text-right">Total</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.tempId}>
                  <TableCell className="p-2">
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => handleUpdateItem(item.tempId, 'quantity', parseFloat(e.target.value) || 0)}
                      className="h-8 text-center"
                      disabled={disabled}
                    />
                  </TableCell>
                  <TableCell className="p-2">
                    <Input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleUpdateItem(item.tempId, 'description', e.target.value)}
                      placeholder="Descrição do item/serviço"
                      className="h-8"
                      disabled={disabled}
                    />
                  </TableCell>
                  <TableCell className="p-2">
                    <Input
                      type="text"
                      value={formatBrazilianNumber(item.unit_price)}
                      onChange={(e) => handleCurrencyChange(item.tempId, 'unit_price', e.target.value)}
                      placeholder="0,00"
                      className="h-8 text-right"
                      disabled={disabled}
                    />
                  </TableCell>
                  <TableCell className="p-2 text-right font-medium">
                    R$ {formatBrazilianNumber(item.total_price)}
                  </TableCell>
                  <TableCell className="p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleRemoveItem(item.tempId)}
                      disabled={disabled}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              
              {/* Linha de total */}
              <TableRow className="bg-muted/30 font-medium">
                <TableCell colSpan={3} className="text-right p-2">
                  Total Geral:
                </TableCell>
                <TableCell className="text-right p-2">
                  R$ {formatBrazilianNumber(totalGeral)}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="border rounded-lg p-4 text-center text-muted-foreground text-sm">
          Nenhum item adicionado. Clique em "Adicionar" para incluir itens detalhados.
        </div>
      )}
      
      {items.length > 0 && (
        <p className="text-xs text-muted-foreground">
          O custo total da OS será calculado automaticamente com base nos itens.
        </p>
      )}
    </div>
  );
}
